/**
 * История выдач с откатом (16.10.2026, владелец: «нужно видеть историю выдач с возможностью отката»).
 * PayoutList — строки выдач: когда, кому, сколько, кто выдал, комментарий, «холд был»; «Отменить» → инлайн «точно?
 * да/нет» (A3: без native confirm), двойное нажатие гасит busy-ref; отменённая остаётся зачёркнутой с «кто и когда».
 * StaffHistory — раскрытая строка «Холда»: старт на 09.10 (16.10, владелец; ОО правит), заработок по закрытым неделям,
 * идущая неделя и выдачи одного человека.
 * AllPayouts — вкладка «История выдач»: все выдачи, новые сверху.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { NumPadPop } from "../../components/shared";
import { cancelPayout, fetchPayouts, fetchWeeks, orenStamp, putOpening, rub, signedRub, weekLabel, type Opening, type Payout, type Week } from "../../hooks/payoutsApi";
import { errText } from "./BonusPenaltySheet";
import css from "./hold.module.css";

export function PayoutList({ rows, showName, onChanged, say }: { rows: Payout[]; showName: boolean; onChanged: () => void; say: (t: string, err?: boolean) => void }) {
  const [ask, setAsk] = useState<number | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const busyRef = useRef(false);
  const cancel = async (p: Payout) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(p.id);
    try {
      const r = await cancelPayout(p.id);
      say(r.already ? "Эта выдача уже была отменена" : `Выдача ${rub(p.amount)} отменена · холд ${signedRub(r.hold_after)}`);
      setAsk(null); onChanged();
    } catch (e) { say(errText(e), true); } finally { busyRef.current = false; setBusy(null); }
  };
  if (!rows.length) return <div className={css.empty}>Выдач пока не было</div>;
  return (
    <div className={css.pList}>
      {rows.map((p) => {
        const off = !!p.cancelled_at;
        return (
          <div key={p.id} className={`${css.pRow} ${off ? css.pOff : ""}`}>
            <div className={css.pTop}>
              <span className={css.pWhen}>{orenStamp(p.paid_at)}</span>
              {showName ? <span className={css.pName}>{p.staff_name || p.staff_id}</span> : <span className={css.pName}>выдано</span>}
              <span className={css.pAmt}>{rub(p.amount)}</span>
            </div>
            <div className={css.pMeta}>
              {p.created_by_name || "ОО"}{p.hold_before != null ? ` · холд был ${signedRub(p.hold_before)}` : ""}{p.comment ? ` · ${p.comment}` : ""}
            </div>
            {off ? (
              <div className={css.pCancelled}>отменена {orenStamp(p.cancelled_at)} · {p.cancelled_by_name || "ОО"}{p.cancel_reason ? ` · ${p.cancel_reason}` : ""}</div>
            ) : ask === p.id ? (
              <div className={css.ask}>Отменить выдачу {rub(p.amount)}?
                <button type="button" className={css.yes} disabled={busy === p.id} onClick={() => void cancel(p)}>{busy === p.id ? "…" : "да, отменить"}</button>
                <button type="button" className={css.no} disabled={busy === p.id} onClick={() => setAsk(null)}>нет</button>
              </div>
            ) : (
              <div className={css.pActs}><button type="button" className={css.act} onClick={() => setAsk(p.id)}>Отменить</button></div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function StaffHistory({ staffId, tick, onChanged, say }: { staffId: string; tick: number; onChanged: () => void; say: (t: string, err?: boolean) => void }) {
  const [weeks, setWeeks] = useState<{ weeks: Week[]; current: Week; opening: Opening | null } | null>(null);
  const [np, setNp] = useState<HTMLElement | null>(null);
  const saving = useRef(false);
  const saveOpening = async (v: number) => {
    if (saving.current) return;
    saving.current = true;
    try { const r = await putOpening(staffId, Math.max(0, Math.round(v))); say(`Старт на 09.10: ${rub(Math.max(0, Math.round(v)))} · холд ${signedRub(r.hold_after)}`); onChanged(); }
    catch (e) { say(errText(e), true); } finally { saving.current = false; }
  };
  const [rows, setRows] = useState<Payout[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setErr(null);
    Promise.all([fetchWeeks(staffId), fetchPayouts(staffId)]).then(
      ([w, p]) => { if (alive) { setWeeks(w); setRows(p); } },
      (e: unknown) => { if (alive) setErr(errText(e)); },
    );
    return () => { alive = false; };
  }, [staffId, tick]);
  if (err) return <div className={css.err}>{err}</div>;
  if (!weeks || !rows) return <div className={css.empty}>Загружаю историю…</div>;
  return (
    <div className={css.hist}>
      <div className={css.hWeek}>
        <span>Старт на 09.10{weeks.opening?.note ? ` · ${weeks.opening.note}` : ""}</span>
        <button type="button" className={css.act} onClick={(e) => setNp(e.currentTarget)} title="Стартовый холд — изменить">{signedRub(weeks.opening?.amount ?? 0)}</button>
      </div>
      <NumPadPop open={!!np} anchor={np} label="Старт холда на 09.10" mode="money" value={weeks.opening?.amount || ""} max={300000}
        onCommit={(v) => { if (Math.round(v) !== (weeks.opening?.amount ?? 0)) void saveOpening(v); }} onClose={() => setNp(null)} />
      {weeks.weeks.map((w) => (
        <div key={w.from} className={css.hWeek}><span>Неделя {weekLabel(w)} · заработал</span><b>{signedRub(w.earned)}</b></div>
      ))}
      <PayoutList rows={rows} showName={false} onChanged={onChanged} say={say} />
      <div className={css.hCur}>Идёт неделя {weekLabel(weeks.current)}: {signedRub(weeks.current.earned)} · войдёт в холд в пятницу</div>
    </div>
  );
}

export function AllPayouts({ tick, onChanged, say }: { tick: number; onChanged: () => void; say: (t: string, err?: boolean) => void }) {
  const [rows, setRows] = useState<Payout[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    let alive = true;
    setErr(null);
    fetchPayouts().then((r) => { if (alive) setRows(r); }, (e: unknown) => { if (alive) setErr(errText(e)); });
    return () => { alive = false; };
  }, []);
  useEffect(() => load(), [load, tick]);
  if (err) return <div className={css.err}>{err}</div>;
  if (!rows) return <div className={css.empty}>Загружаю…</div>;
  return <PayoutList rows={rows} showName onChanged={onChanged} say={say} />;
}
